/**
 * Tier detection (SPEC-01 §2, P0-T04).
 * Order: URL query ?tier= (SPEC-13 §2.1 runner) > URL hash #low|#mid|#high >
 * saved setting (localStorage kn_tier) > GPU string table > unknown-device
 * default. Optional 3-second offscreen-canvas benchmark on its own WebGL 2
 * engine, disposed before the main engine is made.
 */
import type { Tier } from '@kota-nova/shared';
import { tierRenderScale } from '@kota-nova/shared';

export interface TierInfo {
  tier: Tier;
  gpu: string;
  source: 'hash' | 'saved' | 'gpu-table' | 'unknown-default' | 'benchmark';
  benchmarkMs?: number; // benchmark p95 frame time, if it ran
  renderScale: number;
}

const HASH_RE = /#(low|mid|high)\b/;

/** `?tier=` (SPEC-13 §2.1 runner URLs) then `#low/#mid/#high`; null if neither. */
export function tierFromHash(
  hash: string = typeof location !== 'undefined' ? location.hash : '',
  search: string = typeof location !== 'undefined' ? location.search : '',
): Tier | null {
  const q = /[?&]tier=(low|mid|high)\b/.exec(search);
  if (q) return q[1] as Tier;
  const m = HASH_RE.exec(hash);
  return m ? (m[1] as Tier) : null;
}

export function savedTier(): Tier | null {
  try {
    const v = localStorage.getItem('kn_tier');
    return v === 'low' || v === 'mid' || v === 'high' ? v : null;
  } catch {
    return null; // private mode
  }
}

export function saveTier(tier: Tier): void {
  try {
    localStorage.setItem('kn_tier', tier);
  } catch {
    /* ignore */
  }
}

/** GPU renderer string: WebGPU adapter info if present, else WEBGL_debug_renderer_info. */
export async function readGpuString(): Promise<string> {
  const nav = navigator as Navigator & { gpu?: GPU };
  if (nav.gpu?.requestAdapter) {
    try {
      const adapter = await nav.gpu.requestAdapter();
      const info = adapter?.info as { description?: string; device?: string } | undefined;
      const desc = info?.description ?? info?.device;
      if (desc) return desc;
    } catch {
      /* fall through to WebGL */
    }
  }
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = ext ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string) : '';
      const lose = gl.getExtension('WEBGL_lose_context');
      lose?.loseContext();
      if (renderer) return renderer;
    }
  } catch {
    /* ignore */
  }
  return 'unknown';
}

/** GPU-string table (SPEC-01 §2.1). "Apple GPU" is what Safari reports on every Apple device. */
export function tierFromGpuString(gpu: string, isMobileUa: boolean): Tier {
  const s = gpu.toLowerCase();
  const has = (...subs: string[]): boolean => subs.some((x) => s.includes(x));

  // High: NVIDIA RTX / GTX 16xx+, AMD RX 5000+
  if (has('rtx')) return 'high';
  if (/\bgtx\s*1[6-9]\d{2}|\bgtx\s*2\d{3}/.test(s)) return 'high';
  if (/\brx\s*[5-9]\d{3}|\brx\s*9\d{3}/.test(s)) return 'high';

  // Low: older mobile / integrated
  if (
    has(
      'mali-g5',
      'mali-g6',
      'adreno 3',
      'adreno 4',
      'adreno 5',
      'powervr',
      'intel(r) hd',
      'intel hd',
      'intel(uhd 6',
      'intel uhd 6',
    )
  )
    return 'low';
  if (has('mali-t', 'adreno (tm) 3', 'adreno (tm) 4', 'adreno (tm) 5')) return 'low';

  // Mid: modern mobile / Apple / recent integrated
  if (
    has(
      'adreno 6',
      'adreno 7',
      'mali-g7',
      'apple gpu',
      'apple m',
      'm1',
      'm2',
      'm3',
      'm4',
      'iris xe',
      'iris(xe)',
    )
  )
    return 'mid';
  if (has('intel(r) iris')) return 'mid';
  if (has('apple')) return 'mid'; // Safari's "Apple GPU" and friends

  // unknown
  return isMobileUa ? 'low' : 'mid';
}

/** Minimal WebGPU types so the client tsconfig needs no GPU lib. */
interface GPU {
  requestAdapter(): Promise<{ info?: unknown } | null>;
}

/**
 * 3-second benchmark on a **separate offscreen canvas with its own WebGL 2
 * engine** (SPEC-01 §2.2), disposed before the main engine starts: a canvas that
 * already holds a WebGL context can never be given a WebGPU one, and holding two
 * engines is exactly what the spec wants avoided. We render a heavy fallback
 * scene and sample frame times.
 */
export async function runBenchmark(timeoutMs = 3000): Promise<{ p95: number; samples: number } | null> {
  const babylon = await import('babylonjs');
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 360;
  let engine: import('babylonjs').Engine | null = null;
  try {
    const EngineCtor = babylon.Engine as unknown as new (
      c: HTMLCanvasElement,
      aa: boolean,
      o: Record<string, unknown>,
    ) => import('babylonjs').Engine;
    engine = new EngineCtor(canvas, false, { failIfMajorPerformanceCaveat: false });
    const scene = new babylon.Scene(engine);
    const cam = new babylon.FreeCamera('b', new babylon.Vector3(0, 1.6, -8), scene);
    cam.setTarget(babylon.Vector3.Zero());
    const mat = new babylon.StandardMaterial('b', scene);
    mat.diffuseColor = new babylon.Color3(0.7, 0.7, 0.75);
    const box = babylon.MeshBuilder.CreateBox('b-box', { size: 1 }, scene);
    box.material = mat;
    // ~120 draws of ~2.5k-tri spheres ≈ 300k triangles
    const sphere = babylon.MeshBuilder.CreateSphere('b-sph', { segments: 32, diameter: 1 }, scene);
    sphere.material = mat;
    const instances: import('babylonjs').AbstractMesh[] = [];
    for (let i = 0; i < 120; i++) {
      const m = (i % 2 === 0 ? box : sphere).createInstance(`i${i}`);
      const a = (i / 120) * Math.PI * 2;
      m.position.set(Math.cos(a) * (4 + (i % 5)), Math.sin(a * 3) * 2 + 2, Math.sin(a) * (4 + (i % 5)));
      instances.push(m);
    }
    box.freezeWorldMatrix?.();

    const frameTimes: number[] = [];
    const start = performance.now();
    let raf = 0;
    await new Promise<void>((resolve) => {
      const loop = (): void => {
        frameTimes.push(engine!.getDeltaTime());
        engine!.beginFrame();
        scene.render();
        engine!.endFrame();
        if (performance.now() - start >= timeoutMs) resolve();
        else raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });
    cancelAnimationFrame(raf);
    safe(() => instances.forEach((m) => m.dispose()));
    safe(() => sphere.dispose());
    safe(() => box.dispose());
    safe(() => cam.dispose());
    safe(() => mat.dispose());
    safe(() => scene.dispose());
    safe(() => engine?.dispose());
    engine = null;

    const trimmed = frameTimes.slice(10).filter((t) => t > 0 && t < 1000);
    if (trimmed.length < 30) return null;
    const sorted = [...trimmed].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? sorted[sorted.length - 1] ?? 0;
    return { p95, samples: trimmed.length };
  } catch {
    return null;
  } finally {
    try {
      engine?.dispose();
    } catch {
      /* already disposed */
    }
  }
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    /* disposal races on SwiftShader are not boot-fatal */
  }
}

const ORDER: Tier[] = ['low', 'mid', 'high'];
function drop(t: Tier): Tier {
  const i = ORDER.indexOf(t);
  return ORDER[Math.max(0, i - 1)] as Tier;
}

export interface DetectOptions {
  /** Skip the benchmark (tests, or when a saved/hash override already decided). */
  skipBenchmark?: boolean;
  isMobile?: boolean;
  /** total budget for detection incl. benchmark; SPEC gate: ≤ 3.5 s */
  benchmarkMs?: number;
}

export async function detectTier(opts: DetectOptions = {}): Promise<TierInfo> {
  const isMobile = opts.isMobile ?? /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  const gpu = await readGpuString();

  const hash = tierFromHash();
  if (hash) return finish(hash, gpu, 'hash');
  const saved = savedTier();
  if (saved) return finish(saved, gpu, 'saved');

  let tier = tierFromGpuString(gpu, isMobile);
  const source: TierInfo['source'] = tier === 'mid' && gpu === 'unknown' ? 'unknown-default' : 'gpu-table';

  if (!opts.skipBenchmark) {
    const bench = await runBenchmark(opts.benchmarkMs ?? 3000);
    if (bench) {
      if (bench.p95 > 40) tier = drop(tier);
      else if (bench.p95 < 12 && tier === 'mid') tier = 'high';
      return finish(tier, gpu, 'benchmark', bench.p95);
    }
  }
  return finish(tier, gpu, source);
}

function finish(tier: Tier, gpu: string, source: TierInfo['source'], benchmarkMs?: number): TierInfo {
  const info: TierInfo = { tier, gpu, source, renderScale: tierRenderScale(tier) };
  if (benchmarkMs !== undefined) info.benchmarkMs = benchmarkMs;
  return info;
}
